import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useAssociateActions from '../../../hooks/APIHooks/users/useAssociateActions';
import useFeatureActions from '../../../hooks/APIHooks/users/useFeatureActions';
import useFeaturePermissionActions from '../../../hooks/APIHooks/users/useFeaturePermissionActions';
import useRolePermissionsActions from '../../../hooks/APIHooks/users/useRolePermissionsActions';
import { SelectData } from '../../../types/common';
import {
  FeaturePermissionData,
  PermissionReqBody,
  Permissions,
} from '../../../types/permissions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import RolePermissionTable from '../../SetupSubPageComps/RolePermissionsComps/RolePermissionTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import IndicationBadge from '../IndicationBadge';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaModalLoading from '../Loadings/AcademiaModalLoading';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const UpdatePermissionsModal = ({
  user,
  userRole,
  handleModalClose,
}: {
  user: SelectData;
  userRole: SelectData;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [initialValues, setInitialValues] = useState<{ [key: string]: any }>(
    {}
  );
  const [roleBasedPermissions, setRoleBasedPermissions] = useState<{
    [key: string]: any;
  }>({});

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getFeatureListHandler } = useFeatureActions('getFeatureList');
  const { getUserFeaturePermissionsById } = useAssociateActions(
    'userFeaturePermissions',
    user.id
  );
  const { getRolePermissionsByRoleIdHandler } = useRolePermissionsActions(
    '',
    userRole.id
  );
  const { updateFeaturePermissionHandler } = useFeaturePermissionActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  console.log('USER PERMISSIONS: ', getUserFeaturePermissionsById.data?.data);

  // setting permission values after fetching from API here
  useEffect(() => {
    if (
      getRolePermissionsByRoleIdHandler.data?.is_success &&
      getFeatureListHandler.data?.is_success
    ) {
      const roleWisePermissions =
        getRolePermissionsByRoleIdHandler.data.data.role_permissions;

      let userSpecificPermissions: Array<FeaturePermissionData> = [];

      if (getUserFeaturePermissionsById.data?.is_success) {
        userSpecificPermissions =
          getUserFeaturePermissionsById.data.data.feature_permission_data;
      }

      getFeatureListHandler.data.data.forEach((feature) => {
        const roleWiseFeaturePermissions =
          roleWisePermissions.find(
            (permission) => permission.feature === feature.name
          )?.permission_names || [];

        const userSpecificFeaturePermissions =
          userSpecificPermissions.find(
            (permission) => permission.feature === feature.name
          )?.permission_names || [];

        const allFeaturePermissions = roleWiseFeaturePermissions.concat(
          userSpecificFeaturePermissions
        );

        setInitialValues((state) => ({
          ...state,
          [`${feature.name}_create`]: allFeaturePermissions.includes('CREATE'),
          [`${feature.name}_read`]: allFeaturePermissions.includes('READ'),
          [`${feature.name}_update`]: allFeaturePermissions.includes('UPDATE'),
          [`${feature.name}_delete`]: allFeaturePermissions.includes('DELETE'),
        }));

        setRoleBasedPermissions((state) => ({
          ...state,
          [`${feature.name}_create`]:
            roleWiseFeaturePermissions.includes('CREATE'),
          [`${feature.name}_read`]: roleWiseFeaturePermissions.includes('READ'),
          [`${feature.name}_update`]:
            roleWiseFeaturePermissions.includes('UPDATE'),
          [`${feature.name}_delete`]:
            roleWiseFeaturePermissions.includes('DELETE'),
        }));
      });
    }
  }, [
    getFeatureListHandler.data,
    getRolePermissionsByRoleIdHandler.data,
    getUserFeaturePermissionsById.data,
  ]);

  // handler function to handle updating user permissions declared here
  const updateUsersPermissionsHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log('UPDATE PERMISSION VALUES: ', values);

      const featurePermissions: Record<string, Array<Permissions>> = {};

      Object.entries(values)
        .filter(([key, value]) => !roleBasedPermissions[key])
        .forEach(([key, value]) => {
          if (value) {
            const [featureName, permission] = key.split('_');

            if (!featurePermissions[featureName]) {
              featurePermissions[featureName] = [];
            }

            featurePermissions[featureName].push(
              permission.toUpperCase() as Permissions
            );
          }
        });

      const selectedUserPermissions = Object.entries(featurePermissions)
        .filter(([feature, permission_names]) => feature !== 'update')
        .map(([feature, permission_names]) => ({
          feature,
          permission_names,
        }));

      console.log('UPDATE PERMISSION VALUES: ', selectedUserPermissions);

      const body: PermissionReqBody = {
        user: user.id,
        feature_permission_data: selectedUserPermissions,
        updated_by: currentUser?.username,
      };

      await updateFeaturePermissionHandler
        .mutateAsync({ userId: user.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Permissions Updated!!');

            handleModalClose();

            queryClient.invalidateQueries({
              queryKey: ['user-feature-permissions', currentUser?.id],
            });
          } else {
            toast.error(res.message || 'Permission Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering update permission modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[800px]'>
      <ModalTitle titleText='update permissions' />

      <Formik
        initialValues={{ update_permissions: false, ...initialValues }}
        onSubmit={updateUsersPermissionsHandler}
        enableReinitialize
      >
        {(props) => (
          <Form>
            <div className='flex flex-col md:flex-row gap-2 md:items-center justify-between mb-4'>
              <div className='w-full md:w-fit text-legal-dark-blue italic border-2 border-legal-dark-blue border-dashed px-4 py-1 rounded-lg'>
                <div className='flex items-center gap-1'>
                  <p className='font-light'>User's Name</p>
                  <p>:</p>
                  <p className='font-semibold'>{user.name}</p>
                </div>

                <div className='flex items-center gap-1'>
                  <p className='font-light'>User's Role</p>
                  <p>:</p>
                  <p className='font-semibold'>{userRole.name}</p>
                </div>
              </div>

              <div className='border-2 border-dashed border-legal-dark-blue px-4 py-1 rounded-md'>
                <AcademiaCheckboxField
                  name='update_permissions'
                  label='Give User Extra Privilege?'
                />
              </div>
            </div>

            {getFeatureListHandler.isLoading ? (
              <AcademiaModalLoading />
            ) : getFeatureListHandler.isSuccess ? (
              <>
                <div>
                  <RolePermissionTable
                    featureList={getFeatureListHandler.data.data}
                    defaultPermissionValues={roleBasedPermissions}
                    isReadOnly={!props.values.update_permissions}
                  />

                  <div className='mt-2 flex items-center justify-center md:justify-end gap-3'>
                    <IndicationBadge
                      indicationText='Role Based Permissions'
                      colorHex='#FC7300' //MJA: CHECK THIS HEX #FF8042
                    />
                    <IndicationBadge
                      indicationText='Extra Permissions'
                      colorHex='#35476F'
                    />
                  </div>
                </div>

                <div className='mt-5 space-y-5'>
                  <div className='w-full md:w-3/5 mx-auto'>
                    <AcademiaButton
                      type='submit'
                      variant='premium'
                      isLoading={props.isSubmitting}
                      icon={<RxUpdate className='size-6' />}
                      fullWidth
                    >
                      Update Permissions
                    </AcademiaButton>
                  </div>
                </div>
              </>
            ) : getFeatureListHandler.isError ? (
              <p className='text-base-red font-semibold text-center text-lg'>
                Failed to load features data!
              </p>
            ) : null}
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default UpdatePermissionsModal;
